// services/referral.service.js
//
// ⚠️ USE `apiClient`. NOT `jsonApiClient`. This is not a style preference.
//
// Neither referral endpoint returns a JSON:API document. MemberReferralCodeResource
// and MemberReferralListItemResource are hand-built plain-JSON payloads — a bare
// `{ data: { code, shareLink, … } }` with no `type`, no `id`, no `attributes` —
// despite the code resource's own docblock claiming it is "hand-built like
// PoolShareSummaryResource", which DOES emit all three.
//
// Routed through jsonApiClient, jsona deserialization silently guts them:
//
//     GET /referral-code  ->  {}                          every field destroyed
//     GET /referrals      ->  [{ id: "8123456789012" }]   only `id` survives
//
// and it does NOT throw, so neither the try/catch in jsonApiTransformer.js nor the
// one in axios.js transformJsonApiResponse() ever fires. The failure mode is an
// empty screen with a 200 in the network tab and nothing in the console. Measured,
// not theoretical.
//
// `Accept: application/json` is safe here: the route group carries only
// auth:sanctum + throttle:30,1 — no JSON:API media-type middleware to offend.
//
// tests/unit/composables/useMemberReferral.test.js pins the client identity. If you
// change the import below, that test fails. That is the point.
import { apiClient } from '@/httpConfig/config/axios';

export const referralService = {
  /**
   * Mint-or-fetch the caller's referral code, plus the program's current reward
   * rates and the three money buckets.
   *
   * Idempotent — the first call creates the code, every later call returns the
   * same row. Never touches money, grants or wallets, so it is safe to call on
   * every screen mount.
   *
   * @returns {Promise<import('axios').AxiosResponse>} raw body: `{ data: {…} }`
   */
  getCode: () => apiClient.get('/referral-code'),

  /**
   * Every person the caller referred, newest first, capped at 100 rows.
   *
   * Strictly read-only — unlike getCode() it never mints a code. It also has no
   * REFERRAL_PROGRAM_UNAVAILABLE state: rows snapshot their own program, so
   * history stays readable after a program is rotated or deactivated.
   *
   * @returns {Promise<import('axios').AxiosResponse>} raw body: `{ data: [...], meta: {…} }`
   */
  getList: () => apiClient.get('/referrals'),
};
